import { registerActions } from "../actionRegistry";
import { hideLoadingOverlay, withToast, toastSuccess } from "../ui/messages";
import { refreshModal, closeModalLast, modal } from "../ui/modal";
import {
  getDomElement,
  getDomElementOrThrow,
  setText,
  setHtml,
  setStyle,
  queryAll,
  idSuffix,
  fadeIn,
  fadeOut,
} from "../utils/dom";

/** Direct children of the newsletter editor that carry an id (the segment blocks). */
function editorSegments(): HTMLElement[] {
  const editor = getDomElement("editnewsletter-editor");
  if (!editor) return [];
  return Array.from(editor.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.id !== ""
  );
}

/** <option> markup for every collection, read from the hidden inputs on the page. */
function collectionOptionsHtml(): string {
  return Array.from(queryAll<HTMLInputElement>("input.collectionshidden"), (el) => {
    const raw_id = idSuffix(el, "collection-");
    return '<option value="' + raw_id + '">' + el.value + "</option>";
  }).join("");
}
import { randnum } from "../utils/string";
import { api } from "../api";
import { makeSortable } from "../plugins/sortable";
import { renderNewsletterPreview } from "./components/NewsletterPreview";
import type { NewsletterSegment } from "./components/NewsletterPreview";

type PostData = Record<string, string | number>;

export function deletesegment(id: string): void {
  const agree = confirm("Sure you want to delete segment?");
  if (agree) {
    getDomElement("segment-" + id)?.remove();
    updatepreview();
    updatesortable();
  }
}

export function updatesortable(): void {
  const el = document.getElementById("editnewsletter-editor");
  if (!el) return;
  // makeSortable destroys any existing instance first (new segments may have been added)
  makeSortable(el, {
    filter: ".collection_categories_category_allcoll, .button, select, textarea, input",
    preventOnFilter: false,
    onEnd: function () {
      updatepreview();
    },
  });
}

// The reseller and artist newsletter flows are identical except for which api procedure
// they hit; each pair below shares one impl. The exported wrapper NAMES are the registry
// contract (PHP data-fn / inline handlers call them by name) and must not change.

// Opens the sendout modal, which runs the per-recipient loop the retired PHP daemon used
// to run on a cron. These used to call newsletter.send/sendArtist, which only expanded the
// queue and flipped the status to QUEUED — with no daemon left, that marked a newsletter as
// on its way and then nothing ever sent it.
//
// The reseller/artist split is gone from this layer: newsletter.sendPrepare reads
// Newsletter.target_type and expands the right side itself. Both wrappers survive because
// their NAMES are the registry contract.
function sendNewsletterShared(id: string): void {
  modal("newslettersend:" + id, "large");
}

export function sendnewsletter(id: string): void {
  sendNewsletterShared(id);
}

export function sendnewsletterartist(id: string): void {
  sendNewsletterShared(id);
}

async function deleteNewsletterShared(isArtist: boolean): Promise<void> {
  if (!confirm("Are you sure you want to delete the newsletter?")) return;
  const id = getDomElement<HTMLInputElement>("newsletter_id")?.value;
  if (!id) return;
  const deleted = await withToast(
    () => (isArtist ? api.newsletter.deleteArtist({ id }) : api.newsletter.delete({ id })),
    "Newsletter deleted"
  );
  if (deleted) {
    closeModalLast();
    setTimeout(refreshModal, 500);
  }
}

export async function deletenewsletter(): Promise<void> {
  return deleteNewsletterShared(false);
}

export async function deletenewsletterartist(): Promise<void> {
  return deleteNewsletterShared(true);
}

async function hideNewsletterShared(isArtist: boolean): Promise<void> {
  const id = getDomElement<HTMLInputElement>("newsletter_id")?.value;
  if (!id) return;
  const hidden = await withToast(
    () => (isArtist ? api.newsletter.hideArtist({ id }) : api.newsletter.hide({ id })),
    "Newsletter hidden"
  );
  if (hidden) {
    closeModalLast();
    setTimeout(refreshModal, 500);
  }
}

export async function hidenewsletter(): Promise<void> {
  return hideNewsletterShared(false);
}

export async function hidenewsletterartist(): Promise<void> {
  return hideNewsletterShared(true);
}

async function hideNewsletter2Shared(id: string, isArtist: boolean): Promise<void> {
  const hidden = await withToast(
    () => (isArtist ? api.newsletter.hideArtist({ id }) : api.newsletter.hide({ id })),
    "Newsletter hidden"
  );
  if (hidden) refreshModal();
}

export async function hidenewsletter2(id: string): Promise<void> {
  return hideNewsletter2Shared(id, false);
}

export async function hidenewsletter2artist(id: string): Promise<void> {
  return hideNewsletter2Shared(id, true);
}

/** Collect the editor's segment blocks into the `parts` payload shape. */
function collectNewsletterParts(): { type: string; data: string; align?: number }[] {
  let postdatac = 0;
  const postdata: PostData = {};

  for (const child of editorSegments()) {
    const the_id_raw = idSuffix(child, "segment-");
    {
      if (getDomElement("segment-type-" + the_id_raw) != null) {
        const segmentType = getDomElementOrThrow<HTMLInputElement>(
          "segment-type-" + the_id_raw
        ).value;
        postdata[`type_${postdatac}`] = segmentType;
        if (segmentType === "smallcollections") {
          postdata[`data_${postdatac}`] =
            getDomElementOrThrow<HTMLInputElement>("segment-data-" + the_id_raw).value +
            "," +
            getDomElementOrThrow<HTMLInputElement>("segment-data2-" + the_id_raw).value +
            "," +
            getDomElementOrThrow<HTMLInputElement>("segment-data3-" + the_id_raw).value +
            "," +
            getDomElementOrThrow<HTMLInputElement>("segment-data4-" + the_id_raw).value +
            ",";
        } else if (segmentType === "button" || segmentType === "image") {
          postdata[`data_${postdatac}`] =
            getDomElementOrThrow<HTMLInputElement>("segment-data-" + the_id_raw).value +
            ";" +
            getDomElementOrThrow<HTMLInputElement>("segment-data2-" + the_id_raw).value;
        } else {
          let textalign = 0;
          if (getDomElement("align_0_" + the_id_raw) != null) {
            if (getDomElementOrThrow<HTMLInputElement>("align_1_" + the_id_raw).checked) {
              textalign = 1;
            }
            if (getDomElementOrThrow<HTMLInputElement>("align_2_" + the_id_raw).checked) {
              textalign = 2;
            }
          }
          postdata[`data_${postdatac}`] = getDomElementOrThrow<HTMLInputElement>(
            "segment-data-" + the_id_raw
          ).value;
          postdata[`align_${postdatac}`] = textalign;
        }
        postdatac++;
      }
    }
  }

  const parts: { type: string; data: string; align?: number }[] = [];
  for (let i = 0; i < postdatac; i++) {
    parts.push({
      type: postdata[`type_${i}`] as string,
      data: postdata[`data_${i}`] as string,
      align: (postdata[`align_${i}`] as number | undefined) ?? 0,
    });
  }
  return parts;
}

async function saveNewsletterShared(isArtist: boolean): Promise<void> {
  const parts = collectNewsletterParts();
  const id = getDomElementOrThrow<HTMLInputElement>("newsletter_id").value;
  const name = getDomElementOrThrow<HTMLInputElement>("newsletter-name").value;
  const subject = getDomElementOrThrow<HTMLInputElement>("newsletter-subject").value;
  const preheader = getDomElementOrThrow<HTMLInputElement>("newsletter-preheader").value;
  const anonymous = getDomElement<HTMLInputElement>("newsletter-anonymous")?.checked ?? false;

  try {
    if (isArtist) {
      // The welcome-template selector only exists in the artist editor.
      const welcome = Number(getDomElementOrThrow<HTMLInputElement>("newsletter-welcome").value);
      await api.newsletter.saveArtist({ id, name, subject, preheader, welcome, anonymous, parts });
    } else {
      await api.newsletter.save({ id, name, subject, preheader, anonymous, parts });
    }
    toastSuccess("Newsletter saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savenewsletter(): Promise<void> {
  return saveNewsletterShared(false);
}

export async function savenewsletterartist(): Promise<void> {
  return saveNewsletterShared(true);
}

/** A collection's preview image + name, from the hidden inputs the editor holds. */
function readCollection(collId: string): { image: string; name: string } {
  return {
    image: getDomElement<HTMLInputElement>("collection-image-" + collId)?.value ?? "",
    name: getDomElement<HTMLInputElement>("collection-" + collId)?.value ?? "",
  };
}

/**
 * Read the editor's segments out of the DOM, in visual order.
 *
 * The editor DOM is genuinely the source of truth here — Sortable.js reorders the
 * `#editnewsletter-editor` children directly, so their order *is* the newsletter's
 * order, and each segment keeps its state in hidden inputs. So this reads rather
 * than owning state; only the rendering moved into a component.
 */
function readNewsletterSegments(): NewsletterSegment[] {
  const segments: NewsletterSegment[] = [];

  for (const child of editorSegments()) {
    const raw = idSuffix(child, "segment-");
    if (!raw) continue; // non-segment children (spacers etc.)
    if (getDomElement("segment-type-" + raw) == null) continue;

    const data = getDomElementOrThrow<HTMLInputElement>("segment-data-" + raw).value;
    if (data === "") continue; // an empty segment renders nothing
    const segmentType = getDomElementOrThrow<HTMLInputElement>("segment-type-" + raw).value;

    switch (segmentType) {
      case "image":
        segments.push({ kind: "image", src: data });
        break;
      case "header":
        segments.push({ kind: "header", text: data });
        break;
      case "text": {
        // The radios only exist once a text segment has been rendered; absent
        // them the original defaulted to left.
        let align: "left" | "center" | "right" = "left";
        if (getDomElement("align_0_" + raw) != null) {
          if (getDomElementOrThrow<HTMLInputElement>("align_1_" + raw).checked) align = "center";
          if (getDomElementOrThrow<HTMLInputElement>("align_2_" + raw).checked) align = "right";
        }
        segments.push({ kind: "text", text: data, align });
        break;
      }
      case "button":
        segments.push({ kind: "button", text: data });
        break;
      case "largecollection":
        segments.push({ kind: "largecollection", ...readCollection(data) });
        break;
      case "smallcollections": {
        const items: { image: string; name: string }[] = [];
        for (let i = 1; i <= 4; i++) {
          // Slot 1 uses the plain `segment-data-` field; 2-4 are suffixed.
          const dataField = i === 1 ? "segment-data-" : `segment-data${i}-`;
          const collId = getDomElement<HTMLInputElement>(dataField + raw)?.value;
          if (collId) items.push(readCollection(collId));
        }
        segments.push({ kind: "smallcollections", items });
        break;
      }
    }
  }

  return segments;
}

export function updatepreview(): void {
  renderNewsletterPreview(readNewsletterSegments());
}

export function addtonewsletter(type: string, data?: string): void {
  let htmldata = "";
  let segment_id = String(randnum(10000000, 100000000000000));
  let segment_header = "";
  let segment_data = "";

  while (getDomElement("segment-" + segment_id) != null) {
    segment_id = String(randnum(10000000, 100000000000000));
  }

  if (type == "image") {
    segment_header = "Image";
    segment_data =
      '<div class="editnewsletter-segment-field"><img src="' +
      (data ?? "") +
      '"></div><input type="hidden" id="segment-data-' +
      segment_id +
      '" value="' +
      (data ?? "") +
      '"><div class="editnewsletter-segment-fieldtitle">Image URL</div><div class="editnewsletter-segment-field"><input type="text" class="field" id="segment-data2-' +
      segment_id +
      '" placeholder="Image URL"></div>';
  }

  if (type == "header") {
    segment_header = "Header";
    segment_data =
      '<div class="editnewsletter-segment-fieldtitle">Header text</div><div class="editnewsletter-segment-field"><input type="text" class="field" id="segment-data-' +
      segment_id +
      '" placeholder="Header text"></div>';
  }

  if (type == "text") {
    segment_header = "Text";
    segment_data =
      '<div class="editnewsletter-segment-fieldtitle">Text content</div><div class="editnewsletter-segment-field"><textarea class="field" id="segment-data-' +
      segment_id +
      '" placeholder="Text content"></textarea></div><div class="editnewsletter-segment-fieldtitle">Alignment</div><div class="editnewsletter-segment-field"><input class="editnewsletter-segment-field-radio" type="radio" id="align_0_' +
      segment_id +
      '" name="align_' +
      segment_id +
      '" checked><label for="align_0_' +
      segment_id +
      '">Left</label> <input class="editnewsletter-segment-field-radio" type="radio" id="align_1_' +
      segment_id +
      '" name="align_' +
      segment_id +
      '"><label for="align_1_' +
      segment_id +
      '">Center</label> <input class="editnewsletter-segment-field-radio" type="radio" id="align_2_' +
      segment_id +
      '" name="align_' +
      segment_id +
      '"><label for="align_2_' +
      segment_id +
      '">Right</label></div>';
  }

  if (type == "button") {
    segment_header = "Button";
    segment_data =
      '<div class="editnewsletter-segment-fieldtitle">Button label</div><div class="editnewsletter-segment-field"><input type="text" class="field" id="segment-data-' +
      segment_id +
      '" placeholder="Button label"></div><div class="editnewsletter-segment-fieldtitle">Button URL</div><div class="editnewsletter-segment-field"><input type="text" class="field" id="segment-data2-' +
      segment_id +
      '" placeholder="Button URL"></div>';
  }

  if (type == "largecollection") {
    segment_header = "Large collection";
    segment_data =
      '<div class="editnewsletter-segment-fieldtitle">Select a collection</div><div class="editnewsletter-segment-field"><select class="select" id="segment-data-' +
      segment_id +
      '"><option value="">-- Select a collection --</option>';

    segment_data += collectionOptionsHtml();
    segment_data += "</select></div>";
  }

  if (type == "smallcollections") {
    segment_header = "Small collections";

    for (let i = 1; i <= 4; i++) {
      const dataId = i === 1 ? "segment-data-" : `segment-data${i}-`;
      segment_data +=
        '<div class="editnewsletter-segment-fieldtitle">Select collection ' +
        String(i) +
        '</div><div class="editnewsletter-segment-field"><select class="select" id="' +
        dataId +
        segment_id +
        '"><option value="">-- Select a collection --</option>';

      segment_data += collectionOptionsHtml();
      segment_data += "</select></div>";
    }
  }

  if (segment_header && segment_data) {
    htmldata =
      '<div class="editnewsletter-segment" id="segment-' +
      segment_id +
      '"><input type="hidden" id="segment-type-' +
      segment_id +
      '" value="' +
      type +
      '"><div class="editnewsletter-segment-title">Segment: ' +
      segment_header +
      ' <span class="button verysmall red" data-delete-segment="' +
      segment_id +
      '"><i class="fas fa-trash"></i></span></div>' +
      segment_data +
      "</div>";
  }

  if (htmldata != "") {
    getDomElement("editnewsletter-editor")?.insertAdjacentHTML("beforeend", htmldata);
    const seg = getDomElement("segment-" + segment_id);
    // These are the live-preview handlers; they must be bound on the freshly
    // inserted segment, not delegated from an ancestor that pre-dates it.
    for (const el of seg?.querySelectorAll("input[type=text], textarea") ?? []) {
      el.addEventListener("keyup", () => updatepreview());
    }
    for (const el of seg?.querySelectorAll("input[type=radio], select") ?? []) {
      el.addEventListener("change", () => updatepreview());
    }
    for (const el of seg?.querySelectorAll<HTMLElement>("[data-delete-segment]") ?? []) {
      el.addEventListener("click", () => deletesegment(el.dataset.deleteSegment ?? ""));
    }
    getDomElement("segment-data-" + segment_id)?.focus();
    updatesortable();
  }
}

/** What the two create-newsletter modals collect. */
export interface NewNewsletter {
  /** "new" starts blank; "dup" copies an existing newsletter. */
  type: "new" | "dup";
  name: string;
  /** Only meaningful when `type` is "dup". */
  dupId?: string;
}

/**
 * Create a reseller or artist newsletter and open its editor.
 *
 * `createnewsletter` and `createnewsletterartist` were 45-line near-duplicates
 * differing only in which procedure they called and which modal key they opened.
 * Both re-read `#new_name` / `#new_name2` / `#dup_id` out of the DOM even though
 * `SendNewsletterModal` already held all three in `useState` — the values made a
 * round trip through the document for no reason.
 *
 * `#selectedpartners` stays a lookup: it is a CSV hidden input owned by the
 * partner dashboard, not by either modal, so it is genuinely external state.
 * (It is on the DOM-as-IPC list; see todo/dom-seam-campaign.md.)
 */
async function createNewsletterFor(
  target: "reseller" | "artist",
  input: NewNewsletter
): Promise<void> {
  if (!input.name || (input.type === "dup" && !input.dupId)) {
    alert("Missing data");
    return;
  }

  const selected = getDomElement<HTMLInputElement>("selectedpartners");
  const partners = selected
    ? selected.value
        .split(",")
        .map(Number)
        .filter((n) => n > 0)
    : [];

  const payload = {
    type: input.type,
    name: input.name,
    partners,
    ...(input.dupId ? { dupId: input.dupId } : {}),
  };

  try {
    const response =
      target === "artist"
        ? await api.newsletter.createArtist(payload)
        : await api.newsletter.create(payload);
    const key = target === "artist" ? "editnewsletter-artist" : "editnewsletter";
    modal(`${key}:${response.createId}`, "large", 1);
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function createnewsletter(input: NewNewsletter): Promise<void> {
  return createNewsletterFor("reseller", input);
}

export function createnewsletterartist(input: NewNewsletter): Promise<void> {
  return createNewsletterFor("artist", input);
}

export async function newsletterwarnings(): Promise<void> {
  if (getDomElement("newsletter_id") != null) {
    const response = await api.newsletter.warnings({
      id: getDomElementOrThrow<HTMLInputElement>("newsletter_id").value,
    });
    const { errors, error_array } = response;
    if (errors > 0) {
      setText("newsletter-warnings-count", String(errors));
      fadeIn("newsletter-warnings");
      if (getDomElement("selrecipients") != null) {
        for (const [key, value] of Object.entries(error_array)) {
          if (parseInt(key) > 0) {
            if (getDomElement("list-item-error-" + key) != null) {
              setHtml("list-item-error-" + key, value);
              fadeIn("list-item-error-" + key);
              setStyle("list-item-" + key, "color", "red");
            }
          }
        }
      }
    } else {
      if (getDomElement("newsletter-warnings") != null) {
        fadeOut("newsletter-warnings");
      }
    }
  }
}

async function saveRecipientsShared(newsletter_id: string, isArtist: boolean): Promise<void> {
  const recipients: number[] = [];
  for (const el of queryAll<HTMLInputElement>(".lightbox-toggles")) {
    if (el.value === "1") {
      recipients.push(Number(idSuffix(el, "lb-state-")));
    }
  }
  if (recipients.length === 0) return;

  try {
    const response = isArtist
      ? await api.newsletter.saveRecipientsArtist({ id: newsletter_id, recipients })
      : await api.newsletter.saveRecipients({ id: newsletter_id, recipients });
    closeModalLast();
    toastSuccess("Recipients updated");
    setText("to-count", String(response.count));
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function saverecipients(newsletter_id: string): Promise<void> {
  return saveRecipientsShared(newsletter_id, false);
}

export async function saverecipientsartist(newsletter_id: string): Promise<void> {
  return saveRecipientsShared(newsletter_id, true);
}

/** A newsletter image finished uploading — append it as an image segment. */
export function nlUploadDone(imageUrl: string): void {
  addtonewsletter("image", imageUrl);
  hideLoadingOverlay();
  updatepreview();
}

registerActions({
  updatepreview,
  deletesegment,
});
